import type {
  HomeProduct,
  ProductSupplierFeature,
} from '../../../graphProduct';
import type { IProduct } from '../../../product';
import {
  basicHomeIProduct,
  basicHomeProduct,
  getUspsBySellingPoints,
} from '../../../product';
import type { ProductSupplier } from '../travel/types';
import type {
  BusinessCar_CalculationResult,
  BusinessCar_CommonTechnical,
  BusinessCar_PolicyDetails,
} from './types';

export class OSBusinessCarQueryProvider {
  public validateAddressVariables(
    postcode: string,
    houseNumber: number,
    houseNumberAddition?: string
  ): object {
    return {
      postalCode: postcode,
      houseNumber,
      houseNumberAddition,
    };
  }

  public calculationInputVariables(comparisonId: string) {
    return {
      comparisonId: comparisonId,
    };
  }

  public validateEmailVariables(
    comparisonId: string | number,
    email: string,
    productType: string
  ): object {
    return {
      comparisonId,
      email,
      productType,
    };
  }

  public validateSuppliersVariables(
    result: BusinessCar_CalculationResult
  ): object {
    const productKrns = result.policyDetails?.map((item) => {
      return item.productCode;
    });

    return {
      productKrns: productKrns,
      productCategory: 'BUSINESS_CAR',
    };
  }

  public calculateBusinessCarVariables(
    searchCriteria: any,
    selectedFilters: object,
    clientId: string | undefined,
    refKey?: string
  ): object {
    const filters = { ...selectedFilters };
    const paymentTermInMonths =
      filters['showPriceMode']?.[0] === 'month' ? 1 : 12;
    const today = new Date().toISOString().split('T')[0];
    const address = searchCriteria.insuredIsRegularDriver
      ? {
          postalCode: searchCriteria.postcode,
          houseNumber: searchCriteria.housenumber,
          houseNumberAddition: searchCriteria.housenumberaddition,
        }
      : {
          postalCode: searchCriteria.kvkPostcode,
          houseNumber: searchCriteria.kvkHousenumber,
          houseNumberAddition: searchCriteria.kvkHousenumberaddition,
        };

    function convertDateFormat(dateString) {
      const parts = dateString.split('-');

      const day = parts[0];
      const month = parts[1];
      const year = parts[2];

      return `${year}-${month}-${day}`;
    }

    const date = convertDateFormat(searchCriteria.birthdate);
    return {
      input: {
        request: {
          policyDetails: [
            {
              ...(refKey ? { refKey: refKey } : {}),
              effectiveDate: today,
              partyPolicyHolders: [
                {
                  tradeRegisterNumber: searchCriteria.tradeRegisterNumber,
                  selfEmployedWorker: true, //searchCriteria.selfEmployedWorker ,
                },
              ],
              objectMotorVehicles: [
                {
                  licensePlate: searchCriteria.licensePlate.replaceAll('-', ''),
                  leaseFinancingRent: 'VE', // Fixed value as per original code
                  dealerExtrasValue: searchCriteria.dealerExtrasValue || 0,
                  presentTnoScmVehicleSecurityClass:
                    searchCriteria.securitySystemClass === ''
                      ? 'V1'
                      : searchCriteria.securitySystemClass,
                  vatDeductible: searchCriteria.vatDeductible,
                  motorVehicleUse: searchCriteria.motorVehicleUse,
                  annualMileage: Number(searchCriteria.kmPerYear),
                  rdwVehicleCategory: searchCriteria.rdwVehicleCategory,
                  coverage: searchCriteria.cartype,
                },
              ],
              partyRegularDrivers: [
                {
                  insuredIsRegularDriver:
                    searchCriteria.insuredIsRegularDriver === 'false',

                  ...(searchCriteria.insuredIsRegularDriver === 'false'
                    ? {
                        birthDate: date,
                        postalCode: address.postalCode,
                        houseNumber: address.houseNumber,
                        houseNumberAddition: address.houseNumberAddition,
                      }
                    : {}),
                  numberOfClaimFreeYears: Number(searchCriteria.claimFreeYears),
                },
              ],
              ...(searchCriteria.deductibleAmount !== 'null'
                ? {
                    deductibleAmount: Number(searchCriteria.deductibleAmount),
                  }
                : {}),
              paymentTermInMonths,
              returnCommissionPercentage: 0,
            },
          ],
          commonFunctional: [
            {
              porIdBroker: '00436',
            },
          ],
          commonTechnical: [
            {
              senderId: clientId,
            },
          ],
        },
        productLimit: 10,
      },
    };
  }

  public formatBusinessCarProduct(
    credentials: any,
    packages: BusinessCar_PolicyDetails[],
    products: HomeProduct[],
    suppliers: ProductSupplier[],
    commonTechnical: BusinessCar_CommonTechnical[],
    selectedFilters: any,
    searchCriteria: any
  ): IProduct[] {
    return packages
      .filter((item) => item.totalInstallmentAmount !== null)
      .map((policyDetails) => {
        const {
          customProperties,
          productCode,
          productDescription,
          totalInstallmentAmount,
          grossPremiumInstallment,
          surchargesInstallmentAmount,
          discountsInstallmentAmount,
          netPremiumInstallment,
          costsInstallmentAmount,
          insuranceTaxInstallmentAmount,
          paymentTermInMonths,
          renewalCommissionInstallmentAmount,
          objectMotorVehicles,
          conditionConditionDetails,
          orderFormId,
          refKey,
          companyNameDescription,
          documents: extraDocs,
        } = policyDetails;

        const product: HomeProduct = {
          ...basicHomeProduct,
          ...products.find((p) => p.krn === productCode),
        };

        const supplier: any = {
          ...suppliers.find((s) => s.krn === customProperties?.supplierKrn),
        };

        const conditions =
          conditionConditionDetails?.map((condition) => ({
            url: condition.url,
            description: condition.conditionText,
            conditionNumber: condition.conditionNumber,
          })) || [];

        const filteredDocs = extraDocs.filter(
          (doc: any) => doc.attachmentDescription !== 'CompanyLogo'
        );
        const {
          affiliateUrl,
          insuranceCardUrl,
          orderType,
          // productType,
          termsAndConditionsUrl,
          testScores,
          sellingPoints: productSellingPoints,
          customAttributes,
        } = product;
        console.log({ product, supplier, objectMotorVehicles });
        const {
          about,
          advantages,
          disadvantages,
          features,
          howItWorks,
          name,
          nameLogoUrl,
          sellingPoints,
          sustainabilityScore,
          taskDivisions,
        } = supplier;

        return {
          ...basicHomeIProduct,
          refKey,
          commonTechnical: [
            {
              senderId: credentials.clientId,
              sessionId: commonTechnical[0].sessionId,
              transactionReference: commonTechnical[0].transactionReference,
              accessCode: commonTechnical[0].accessCode,
            },
          ],
          bigProductFeature: {
            ...basicHomeIProduct.bigProductFeature,
            decimals: 0,
            label: 'Eigen risico',
            hideIfZero: false,
            variant: 'priceMedium',
            value: {
              month:
                objectMotorVehicles?.[0]?.coverageHullVehicles[0]
                  .deductibleAmount || 0,
              year:
                objectMotorVehicles?.[0]?.coverageHullVehicles[0]
                  .deductibleAmount || 0,
            },
          },
          priceMode: selectedFilters['showPriceMode']?.[0],
          id: productCode,
          productName: productDescription,
          productType: 'os-business-car',
          priceInfo: {
            ...basicHomeIProduct.priceInfo,
            initial: totalInstallmentAmount,
            total: totalInstallmentAmount,
          },
          image: nameLogoUrl,
          customAttributes,
          customProperties,
          orderType,
          productCode: productCode || '',
          provider: {
            ...basicHomeIProduct.provider,
            description: about,
            howItWorks,
            insuranceCardUrl,
            sellingPoints,
            label: name,
            name,
            sustainabilityScore:
              sustainabilityScore?.[0]?.score?.toString() || '',
            id: supplier.krn,
            taskDivisions,
            termsAndConditionsUrl,
            customAttributes: supplier.customAttributes,
          },
          sustainabilityScore: sustainabilityScore || '0',
          prosAndCons: {
            cons: {
              ...basicHomeIProduct.prosAndCons.cons,
              title1: disadvantages?.[0]?.description || '',
              title2: disadvantages?.[1]?.description || '',
            },
            pros: {
              ...basicHomeIProduct.prosAndCons.cons,
              title1: advantages?.[0]?.description || '',
              title2: advantages?.[1]?.description || '',
            },
          },
          usps: getUspsBySellingPoints(productSellingPoints),
          type: 'os-business-car',
          purchasableProduct: orderType !== 'NOT_ORDERABLE',
          companyNameDescription,
          coverageDetails: {
            grossPremiumInstallment,
            surchargesInstallmentAmount,
            discountsInstallmentAmount,
            netPremiumInstallment,
            costsInstallmentAmount,
            insuranceTaxInstallmentAmount,
            paymentTermInMonths,
            renewalCommissionInstallmentAmount,
          },
          documents: [
            ...conditions,
            ...filteredDocs.map((doc: any, i) => ({
              url: doc.urlAttachment,
              description: doc.attachmentDescription,
              conditionNumber: i + 1,
            })),
          ],
          vehicleDetails: objectMotorVehicles?.[0] || null,
          orderFormId,
          characteristics: this.buildCharacteristics(features || []),
          affiliateUrl: orderType === 'AFFILIATE' ? affiliateUrl : '',
          termsAndConditionsUrl,
          testScores,
          testScore: testScores?.[0]?.score || 0,
          filters: {
            ...selectedFilters,
          },
          searchCriteria,
        };
      });
  }

  public formatAdditionalCoveragesResult(result: any): any {
    const policyDetails =
      result.calculateAdditionalCoveragesResult?.policyDetails?.[0];

    if (!policyDetails) {
      throw new Error('No policy details found in additional coverages result');
    }

    const { objectMotorVehicles } = policyDetails;

    return {
      additionalCoverages: objectMotorVehicles?.[0]?.coverageAny || [],
    };
  }

  private buildCharacteristics(features: ProductSupplierFeature[]) {
    return (
      features?.map((feature) => ({
        label: feature.name,
        value: feature.value,
      })) || []
    );
  }
}
